import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';

import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Card } from 'ui-components/components/card';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalCloseButton } from 'ui-components/components/modal/modal-close-button';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { FormattedCurrency } from '../../../components/formatted-currency';
import { ModalPortal } from '../../../components/modal-portal';
import {
  AUTOMATION_RECOMMENDATION_EXPLANATION_CLICKED,
  AUTOMATION_RECOMMENDATION_RECOMMENDATIONS_SHOWN,
} from '../../../constants/analytics-events';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';
import { Segment } from '../../../services/analytics-services/segment';
import { LogService } from '../../../services/log-service';
import { useAutomationRecommendationsContext } from '../automation-recommendations-context';
import messages from './messages';
import style from './style.module.less';
import { ContactSalesButton } from '../contact-sales-button';

/**
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {() => void} props.openContactModal
 * @param {(id: string) => void} props.openExplanationModal
 * @returns {JSX.Element}
 */
export const AutomationRecommendationsModal = ({ onClose, openContactModal, openExplanationModal }) => {
  const { recommendations } = useAutomationRecommendationsContext();

  useOnComponentMount(() => {
    try {
      Segment.trackEvent(AUTOMATION_RECOMMENDATION_RECOMMENDATIONS_SHOWN, {
        recommendations,
      });
    } catch (e) {
      LogService.logError(new Error('Could not contact segment', { cause: e }));
    }
  });

  const onContactSales = () => {
    onClose();
    openContactModal();
  };
  const onExplanationClicked = (id) => {
    onClose();
    openExplanationModal(id);
    try {
      Segment.trackEvent(AUTOMATION_RECOMMENDATION_EXPLANATION_CLICKED, {
        id,
      });
    } catch (e) {
      LogService.logError(new Error('Could not contact segment', { cause: e }));
    }
  };
  return (
    <ModalPortal onClose={onClose}>
      <Modal size='small'>
        <FlexRow className={style['sparkly-header']}>
          <FlexColumn>
            <FlexRow className={style.subheader}>
              <div className={style['sparkle-icon']}>
                <FontAwesomeIcon icon={faSparkles} />
              </div>
              <Text textType={TextType.H4} value={messages.savingsInsight} />
            </FlexRow>
            <Text textType={TextType.H3} value={messages.modalTitle} />
          </FlexColumn>
          <ModalCloseButton className={style.close} closeModal={onClose} />
        </FlexRow>
        <ModalBody className={style.body}>
          {recommendations.map((recommendation) => {
            const { id, thirtyDayProjectedSavings, recommendedChange } = recommendation;
            const { currencyCode, value } = thirtyDayProjectedSavings;
            const { serviceFromName, serviceToName } = recommendedChange;
            return (
              <Card key={id}>
                <Text
                  formattedMessageValues={{
                    currency: <FormattedCurrency currencyCode={currencyCode} value={value} />,
                    fromServiceName: serviceFromName,
                    toServiceName: serviceToName,
                  }}
                  textType={TextType.Paragraph}
                  value={messages.description}
                />
                <div>
                  <Button onClick={() => onExplanationClicked(id)} theme={THEME_CONSTANTS.link}>
                    <FormattedMessage {...messages.saveExplanationLink} />
                  </Button>
                </div>
              </Card>
            );
          })}
          <div className={style['button-wrapper']}>
            <ContactSalesButton buttonText={messages.saveButton} onClick={onContactSales} />
          </div>
          <Text textType={TextType.Caption} value={messages.savingsDisclaimer} />
        </ModalBody>
      </Modal>
    </ModalPortal>
  );
};
